/**
 * Operations (in-stay tasks) feature pages.
 *
 *   TasksPage         — the exceptions-first tasks board (route `tasks`).
 *   TasksRoutingPage  — routing rules admin (route `tasks/routing`).
 *
 * Both are gated by the `in_stay_operations` feature flag + the
 * `service_request:read` permission. Feature-flag/RBAC hiding here is UX only;
 * the route loader + backend remain authoritative.
 *
 * All tenant/hotel access uses RESOLVED UUIDs from `useHotelContext`, never
 * slugs. All data flows through query/mutation hooks — no direct fetches. Every
 * status/urgency is rendered with a text label (never colour alone) and every
 * interactive element has a kebab-case data-testid.
 */

import type { ServiceRequest } from '@hostesa/api-client';
import { useServiceRequests } from '@hostesa/api-client';
import { useAuth } from '@hostesa/auth-client';
import { useFlag } from '@hostesa/feature-flags';
import { useT } from '@hostesa/i18n';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@hostesa/ui';
import { ClipboardList, Route } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';
import { useHotelContext } from '../../lib/hotel-context';
import { ExceptionsPanel } from './exceptions-panel';
import { TaskCard } from './task-card';
import { isActive } from './task-presentation';

/** Shared gate: render children only when flag + permission both pass. */
function useOperationsAccess(): { flagOn: boolean; canRead: boolean } {
  const flagOn = useFlag('in_stay_operations');
  const { can } = useAuth();
  return { flagOn, canRead: can('service_request:read') };
}

function AccessGate({ testId, children }: { testId: string; children: ReactNode }): ReactElement {
  const { t } = useT('operations');
  const { flagOn, canRead } = useOperationsAccess();

  if (!flagOn) {
    return (
      <div data-testid={`${testId}-disabled`}>
        <EmptyState
          icon={<ClipboardList />}
          title={t('states.disabledTitle')}
          description={t('states.disabledDescription')}
          data-testid={`${testId}-disabled-state`}
        />
      </div>
    );
  }

  if (!canRead) {
    return (
      <div data-testid={`${testId}-forbidden`}>
        <EmptyState
          icon={<ClipboardList />}
          title={t('states.forbiddenTitle')}
          description={t('states.forbiddenDescription')}
          data-testid={`${testId}-forbidden-state`}
        />
      </div>
    );
  }

  return <>{children}</>;
}

// ── Tasks board ──────────────────────────────────────────────────────────────

/** Active (non-terminal) requests grouped into the board's working column. */
function groupActive(tasks: readonly ServiceRequest[]): ServiceRequest[] {
  return tasks.filter((task) => isActive(task.status));
}

function TasksBoard({ tenantId, hotelId }: { tenantId: string; hotelId: string }) {
  const { t } = useT('operations');
  const query = useServiceRequests(tenantId, hotelId);

  const active = useMemo(() => groupActive(query.data ?? []), [query.data]);
  const handledToday = query.data?.length ?? 0;

  return (
    <div className="flex flex-col gap-8" data-testid="operations-board">
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('board.title')}
        description={t('board.description')}
        data-testid="operations-board-header"
        actions={
          <Badge variant="muted" data-testid="operations-handled-today">
            {t('board.handledToday', { count: handledToday })}
          </Badge>
        }
      />

      {/* Exceptions-first: the prominent panel reception reads by default. */}
      <ExceptionsPanel tenantId={tenantId} hotelId={hotelId} />

      <section data-testid="operations-active-section" className="flex flex-col gap-4">
        <h2 className="font-[var(--font-display)] text-lg font-medium tracking-tight text-[var(--color-charcoal)]">
          {t('columns.inProgress')}
        </h2>

        {query.isPending ? (
          <LoadingState title={t('states.loadingTitle')} data-testid="operations-board-loading" />
        ) : query.isError ? (
          <ErrorState
            title={t('states.errorTitle')}
            description={t('states.errorDescription')}
            data-testid="operations-board-error"
          />
        ) : active.length === 0 ? (
          <EmptyState
            icon={<ClipboardList />}
            title={t('exceptions.empty')}
            description={t('exceptions.emptyDescription')}
            data-testid="operations-board-empty"
          />
        ) : (
          <div className="flex flex-col gap-3" data-testid="operations-active-list">
            {active.map((task) => (
              <TaskCard key={task.id} task={task} tenantId={tenantId} hotelId={hotelId} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function TasksPage(): ReactElement {
  const { tenantId, hotelId } = useHotelContext();
  return (
    <AccessGate testId="operations-board">
      <TasksBoard tenantId={tenantId} hotelId={hotelId} />
    </AccessGate>
  );
}

// ── Routing rules admin ──────────────────────────────────────────────────────

interface RoutingRow {
  category: 'housekeeping' | 'maintenance' | 'food_beverage';
  active: boolean;
}

const ROUTING_ROWS: readonly RoutingRow[] = [
  { category: 'housekeeping', active: true },
  { category: 'maintenance', active: false },
  { category: 'food_beverage', active: false },
];

function RoutingRules() {
  const { t } = useT('operations');

  return (
    <div className="flex flex-col gap-8" data-testid="operations-routing">
      <PageHeader
        eyebrow={t('routing.eyebrow')}
        title={t('routing.title')}
        description={t('routing.description')}
        data-testid="operations-routing-header"
        actions={
          <Button asChild variant="secondary" size="sm" data-testid="operations-routing-back-btn">
            <a href="../tasks">{t('board.title')}</a>
          </Button>
        }
      />

      {/*
        No self-serve routing-editor endpoint exists yet, so this is a calm,
        read-only view of the bits that ARE configurable today (the housekeeping
        row), with maintenance/F&B shown as planned. EmptyState explains the gap.
      */}
      <EmptyState
        icon={<Route />}
        title={t('routing.emptyTitle')}
        description={t('routing.emptyDescription')}
        data-testid="operations-routing-empty"
      />

      <Table data-testid="operations-routing-table">
        <TableHeader>
          <TableRow>
            <TableHead>{t('routing.categoryHeading')}</TableHead>
            <TableHead>{t('routing.teamHeading')}</TableHead>
            <TableHead>{t('routing.slaAckHeading')}</TableHead>
            <TableHead>{t('routing.slaDoneHeading')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ROUTING_ROWS.map((row) => (
            <TableRow key={row.category} data-testid={`operations-routing-row-${row.category}`}>
              <TableCell>
                <span className="flex items-center gap-2">
                  {t(`category.${row.category}`)}
                  <Badge
                    variant={row.active ? 'success' : 'muted'}
                    data-testid={`operations-routing-badge-${row.category}`}
                  >
                    {row.active ? t('routing.activeBadge') : t('routing.plannedBadge')}
                  </Badge>
                </span>
              </TableCell>
              <TableCell>{t(`category.${row.category}`)}</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <p className="text-sm text-[var(--color-stone-mid)]">{t('routing.housekeepingNote')}</p>
    </div>
  );
}

export function TasksRoutingPage(): ReactElement {
  return (
    <AccessGate testId="operations-routing">
      <RoutingRules />
    </AccessGate>
  );
}
